import type { CSSProperties } from 'react';
import { C } from '../../../../../lib/design';
import { PAGE, PAGE_HEADER, PAGE_FOOTER } from '../../../../../config/documentStyles';

export const panelStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: C.pageBg,
    position: 'relative',
  },

  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 12,
    padding: 32,
  },

  placeholderText: {
    color: C.textMut,
    fontSize: 14,
    textAlign: 'center' as const,
    margin: 0,
    lineHeight: 1.5,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    gap: 8,
    flexShrink: 0,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },

  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },

  editionBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: C.textSec,
    padding: '1px 5px',
    borderRadius: 3,
    backgroundColor: C.borderAlt,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    flexWrap: 'wrap' as const,
  },

  unifiedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    height: 42,
    minHeight: 42,
    flexShrink: 0,
    borderBottom: '1px solid #e8e8e8',
    backgroundColor: '#fff',
    padding: '0 12px',
    fontSize: 13,
  },

  docCode: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontWeight: 600,
    fontSize: 12,
    color: C.text,
    whiteSpace: 'nowrap' as const,
    marginRight: 6,
    flexShrink: 0,
  },

  headerSep: {
    width: 1,
    height: 20,
    backgroundColor: 'rgba(0,0,0,0.12)',
    margin: '0 10px',
    flexShrink: 0,
  },

  inlineTab: {
    padding: '4px 8px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'color 0.15s',
    whiteSpace: 'nowrap' as const,
    marginBottom: -1,
  },

  editionLabelCompact: {
    fontSize: 11,
    fontWeight: 500,
    color: C.textSec,
    whiteSpace: 'nowrap' as const,
    marginRight: 6,
  },

  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: C.textSec,
    backgroundColor: 'transparent',
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'background 0.15s',
    flexShrink: 0,
  },

  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#FFFFFF',
    backgroundColor: C.azul2,
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'opacity 0.15s',
    flexShrink: 0,
  },

  versionSelect: {
    padding: '3px 6px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    backgroundColor: C.surface,
    color: C.text,
    cursor: 'pointer',
    maxWidth: 180,
  },

  autoSaveBar: {
    fontSize: 10,
    color: C.textMut,
    padding: '3px 12px',
    backgroundColor: C.borderAlt,
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
  },

  tabBar: {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
    gap: 0,
  },

  tabBtn: {
    padding: '6px 14px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'color 0.15s',
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap' as const,
    padding: '6px 10px',
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
    flexShrink: 0,
  },

  toolbarBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 5,
    borderRadius: 3,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: C.textSec,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'inherit',
    lineHeight: 1,
    transition: 'background 0.1s',
  },

  toolbarSep: {
    width: 1,
    height: 18,
    backgroundColor: C.border,
    margin: '0 3px',
    flexShrink: 0,
  },

  styleSelect: {
    padding: '3px 6px',
    fontSize: 11,
    fontFamily: 'inherit',
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    backgroundColor: C.surface,
    color: C.text,
    cursor: 'pointer',
    minWidth: 120,
  },

  zoomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '4px 12px',
    flexShrink: 0,
    backgroundColor: C.surface,
    borderBottom: `1px solid ${C.border}`,
  },

  zoomBtn: {
    width: 24,
    height: 24,
    borderRadius: 5,
    border: `1px solid ${C.border}`,
    background: C.surface,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: C.textSec,
    fontFamily: 'inherit',
    padding: 0,
    flexShrink: 0,
  },

  zoomLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: C.textSec,
    minWidth: 36,
    textAlign: 'center' as const,
    flexShrink: 0,
  },

  contentScroll: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '20px 12px',
    backgroundColor: C.pageBg,
  },

  a4Container: {
    background: '#FFFFFF',
    maxWidth: PAGE.width,
    margin: '0 auto',
    padding: `${PAGE.padTop}px ${PAGE.padSide}px ${PAGE.padBottom}px`,
    boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
    borderRadius: 2,
    minHeight: PAGE.height,
    position: 'relative' as const,
  },

  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
    marginBottom: 16,
    borderBottom: PAGE_HEADER.borderBottom,
    fontFamily: PAGE_HEADER.fontFamily,
    fontSize: PAGE_HEADER.fontSize,
  },
  pageHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 },
  pageHeaderLogo: { height: PAGE_HEADER.logoHeight, width: 'auto', flexShrink: 0 },
  pageHeaderDocName: {
    color: PAGE_HEADER.accentColor,
    fontWeight: 600,
    fontSize: PAGE_HEADER.fontSize,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  pageHeaderRight: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  pageHeaderMeta: {
    color: PAGE_HEADER.mutedColor,
    fontSize: PAGE_HEADER.fontSize,
    fontWeight: 400,
    whiteSpace: 'nowrap' as const,
  },
  pageHeaderSep: { color: PAGE_HEADER.mutedColor, fontSize: PAGE_HEADER.fontSize, margin: '0 2px' },

  pageFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24,
    paddingTop: 8,
    borderTop: PAGE_FOOTER.borderTop,
    fontFamily: PAGE_FOOTER.fontFamily,
    fontSize: PAGE_FOOTER.fontSize,
  },
  pageFooterText: { color: PAGE_FOOTER.color, fontSize: PAGE_FOOTER.fontSize },

  viaPanelOverlay: {
    position: 'absolute' as const,
    inset: 0,
    zIndex: 50,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  viaPanelBackdrop: {
    position: 'absolute' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.15)',
  },
  viaPanelContainer: {
    position: 'relative' as const,
    width: 340,
    height: '100%',
    background: '#fff',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.12)',
    overflowY: 'auto' as const,
    zIndex: 1,
  },

  toast: {
    position: 'absolute' as const,
    bottom: 56,
    left: '50%',
    transform: 'translateX(-50%)',
    background: C.azul1,
    color: '#fff',
    padding: '6px 16px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    zIndex: 100,
    whiteSpace: 'nowrap' as const,
    animation: 'slideIn 0.2s ease',
  },

  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(2px)',
  },

  discardDialog: {
    background: 'white',
    borderRadius: 10,
    padding: '20px 24px',
    width: 360,
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
};
